import React from "react";
import { runInAction, action, extendObservable, reaction } from "mobx";
import { post, get } from "../lib/http";
import { ROUTES } from "./routes";
import { GLOBAL_MODALS } from "./modals";
import { trackAction, setCustomAttribute } from "../tracking";
import locale from "locale2";
import OnboardingActions from "../tracking/actions";
import i18n from "../i18n";
import qs from "query-string";

const queryString = qs.parse(document.location.search);

class AppStore {
  originalCMData = null;
  referralToken = "";
  referredByPersonId = "";
  redirectToken = queryString.redirectToken;
  isLead = queryString.onboard === "1";
  useLeadsOnboardingToken = queryString.onboard === "1";
  isProfileProcessing = queryString.processing === "1";
  campaign = "";
  defaultPrefix = 14422; //blank default

  constructor() {
    extendObservable(
      this,
      {
        currentRoute: null,
        globalModal: GLOBAL_MODALS.NONE,
        expired: false,

        // Modal for terms of use and other legal information (or anything where source is a html page)
        showModal: false,
        modalTitle: "",
        modalBody: "",
        iframeUrl: "",

        // ------ register
        prefixCode: this.defaultPrefix,
        firstName: "",
        lastName: "",
        emailAddress: "",
        confidentAgreement: false,

        // ------ other user info
        locale: locale || "en",
        zoneRegion: "",
        councilMemberId: 0,
        personId: 0,
        isDNC: false,
        hasTNC: false,
        recruitedBy: 0,
        reminderDateUTC: "",
        prefixes: null,
        countries: null,
        country: null,
        currency: "USD",

        userSelectedTimeZone: "",
        countryCodeId: 0,
        phoneNumber: "",
        phoneCountryCode: "",
        phoneNumberValidation: null,
        password: "",

        showAcceptedModal: queryString.accepted === "1",
        showPhoneEntry: true
      },
      null,
      { deep: false }
    );

    reaction(
      () => this.currentRoute,
      route => {
        trackAction(OnboardingActions.global.CurrentView, { pageName: route.name });

        // reset scrolling on route change
        if (document.firstElementChild) {
          document.firstElementChild.scrollTop = 0;
        }
      }
    );

    // already TNC, just go to redirector
    reaction(
      () => this.globalModal === GLOBAL_MODALS.WHATS_NEXT
        && !!this.hasTNC              // already T&C
        && !this.isProfileProcessing, // profile is not ready, show thank you instead
      val => val && this.goToRedirector()
    )

    reaction(
      () => this.locale,
      val => {
        i18n.changeLanguage(val)
      }
    )
  }

  loadUser = action(() => {
    let params = {
      leads: this.useLeadsOnboardingToken ? "1" : "0",
      referralToken: this.referralToken,
      referredByPersonId: this.referredByPersonId
    }
    return get("api/getUser", params)
      .then(user =>
        runInAction(() => {
          if (!!user.councilMemberId) {
            setCustomAttribute("cmid", user.councilMemberId)
            trackAction(OnboardingActions.UserLoaded)
            // store so we can compare for change
            this.originalCMData = user;
            // copy values to store
            Object.keys(user)
              .filter(key => key in this)
              .forEach(key => (this[key] = user[key]));

            if (!!user.preferredCulture) {
              this.locale = user.preferredCulture;
            }

            // don't show phone entry to non-wanderers
            // we need to improve UX to remove frictions before enabling for these folks
            // the value of enabling early for wanderer is that it comes with dup check
            this.showPhoneEntry = false;

            // if user already has a rate, hide the rate section
            if (user.rateAmount > 0) {
              ROUTES.RATE.hidden = true;
            }
          }
          if (this.showPhoneEntry) {
            trackAction('SHOW_PHONE_INPUT')
          }
          if (user.referralTokenError) {
            this.globalModal = GLOBAL_MODALS.INVALID_REFERRAL_TOKEN;
          }
          return user;
        })
      )
      .catch(e =>
        runInAction(() => {
          // handle error first so consumer only has to respond to "then"
          this.globalModal = GLOBAL_MODALS.FATAL_ERROR;
          if (e.message === "session expired") {
            this.emailAddress = e.emailAddress || "";
            this.isDNC = this.isDNC || false;
            this.expired = true;
            if (!!e.preferredCulture) {
              this.locale = e.preferredCulture;
            }
          }
          return {};
        })
      );
  });

  createUser = action(userData => {
    if (this.referralToken) userData.referralToken = this.referralToken;
    if (this.referredByPersonId) userData.referredByPersonId = this.referredByPersonId;

    return post("api/createUser", userData)
      .then(user =>
        runInAction(() => {
          this.councilMemberId = user.cmid;
          this.personId = user["cmid-personid"];
          this.currentRoute = ROUTES.RESUME;
          this.country = user.countryCode;
          this.zoneRegion = user.zoneRegion;
          this.updateUserTimeZone();
          this.updateCurrency();
          setCustomAttribute("cmid", this.councilMemberId);
          trackAction(OnboardingActions.UserCreated);
        })
      )
      .catch(this.handleCreateUpdateError);
  });

  updateUser = action(userData => {
    return post("api/updateUser", userData)
    .then(result =>
      runInAction(() => {
        trackAction(OnboardingActions.UserUpdated)
        this.updateUserTimeZone();
        this.updateCurrency();
        this.currentRoute = ROUTES.RESUME;
      })
    )
    .catch(this.handleCreateUpdateError);
  });

  handleCreateUpdateError = err => runInAction(() => {
    switch (err.error) {
      case "VALIDATION_FAILURE":
        this[err.field + 'Validation'] = err;
        trackAction('VALIDATION_FAILURE', err);
        return false;
      case "CM_EXISTS":
        // only for createUser
        trackAction(err.isDupEmail ? OnboardingActions.CmExists : OnboardingActions.CmExistsByPhone, { duplicateOfCmId: err.dupCmid, duplicatePhoneNumber: err.duplicatePhoneNumber })
        this.isDNC = err.isDNC;
        this.currentRoute = ROUTES.CM_EXISTS;
        break;
      case "CM_EMAIL_ADDRESS_ALREADY_IN_USE":
        // only for updateUser
        return Promise.reject(err);
      default:
        this.globalModal = GLOBAL_MODALS.FATAL_ERROR;
    }
  })

  updateUserTimeZone = () => {
    const timeZoneName = this.userSelectedTimeZone
      || ('Intl' in window && Intl.DateTimeFormat && Intl.DateTimeFormat().resolvedOptions && Intl.DateTimeFormat().resolvedOptions().timeZone);
    if (!!timeZoneName) {
      post("api/updateUserTimeZone", { timeZoneName })
    }
  }

  loadPrefixes = () => {
    get("api/getPrefixes")
    .then((prefixes) => {
      this.prefixes = prefixes
    })
    .catch(err => {
      console.log(err)
    });
  }

  loadCountries = () => {
    return get("api/lookupdata/countries")
    .then((countries) => {
      this.countries = countries
    })
    .catch(err => {
      this.countries = []
      console.log(err)
    });
  }

  goToRedirector = () => {
    get("api/getRedirectContext", { redirectToken: this.redirectToken })
      .then(data => {
        const withSession = !!data.sessionToken ? `&${data.sessionTokenName}=${data.sessionToken}` : '';
        window.location = `${process.env.REACT_APP_REDIRECT_URL}/?token=${data.redirectToken}${withSession}`;
      })
      .catch(ex => {
        console.error('goToRedirector', ex)
        window.location = process.env.REACT_APP_REDIRECT_URL;
      })
  };

  updateCurrency = () => {
    switch (this.country) {
      case 962: 
        this.currency = "JPY";
        break;
      default:
        break;
    }
  }

}

export const appStore = new AppStore();
export const AppStoreContext = React.createContext();
window.appStore = appStore;
